import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cx } from '../../../internal/cx.js';
import { formatNumber } from '../../../internal/format-number.js';
import { formatMoney } from '../../billing/money/index.js';
import { Skeleton } from '../../feedback/skeleton/index.js';
import { VisuallyHidden } from '../../layout/visually-hidden/index.js';
function sortItems(items, sort) {
    const indexed = items.map((item, index) => ({ item, index }));
    if (sort === 'none')
        return indexed;
    const dir = sort === 'asc' ? 1 : -1;
    return indexed.sort((a, b) => {
        if (a.item.value === null || b.item.value === null) {
            if (a.item.value === b.item.value)
                return a.index - b.index;
            return a.item.value === null ? 1 : -1;
        }
        return (a.item.value - b.item.value) * dir || a.index - b.index;
    });
}
/**
 * A ranked list with an inline bar per row (top pages, top referrers,
 * rivals by score). Labels and values are real text; the bars are decorative.
 */
export const BarList = forwardRef(function BarList({ items, label, ranked = false, sort = 'desc', max, limit, overflowLabel, format = 'number', currency, unit, locale, notation = 'standard', formatOptions, formatValue, layout = 'stacked', linkComponent: LinkComponent = 'a', missingLabel = 'No value', emptyState = 'No data yet', loading = false, loadingRows = 5, className, ...rest }, ref) {
    const List = ranked ? 'ol' : 'ul';
    const root = {
        ref,
        ...rest,
        className: cx('mn-bar-list', className),
        'data-layout': layout,
        'data-ranked': ranked || undefined,
    };
    if (loading) {
        return (_jsxs("div", { ...root, "aria-busy": "true", "data-loading": "", children: [_jsx("ul", { className: "mn-bar-list__list", "aria-hidden": true, children: Array.from({ length: loadingRows }, (_, i) => (_jsxs("li", { className: "mn-bar-list__item", children: [_jsx("span", { className: "mn-bar-list__main", children: _jsx(Skeleton, { shape: "text", width: `${60 - i * 6}%` }) }), _jsx("span", { className: "mn-bar-list__track", children: _jsx(Skeleton, { shape: "text", width: `${90 - i * 14}%`, height: "100%" }) })] }, i))) }), _jsx(VisuallyHidden, { children: label ? `Loading ${label}` : 'Loading' })] }));
    }
    if (items.length === 0) {
        return (_jsx("div", { ...root, "data-empty": "", children: _jsx("p", { className: "mn-bar-list__empty", children: emptyState }) }));
    }
    const sorted = sortItems(items, sort);
    const shown = limit !== undefined && limit >= 0 ? sorted.slice(0, limit) : sorted;
    const values = items.map((i) => i.value).filter((v) => v !== null);
    const scale = max ?? Math.max(0, ...values);
    const write = (value, item) => {
        if (formatValue)
            return formatValue(value, item);
        if (format === 'currency') {
            return formatMoney(value, currency ?? '', {
                ...(locale !== undefined && { locale }),
                notation,
                ...(formatOptions?.maximumFractionDigits !== undefined && {
                    maximumFractionDigits: formatOptions.maximumFractionDigits,
                }),
                ...(formatOptions?.minimumFractionDigits !== undefined && {
                    minimumFractionDigits: formatOptions.minimumFractionDigits,
                }),
            });
        }
        const text = formatNumber(value, {
            kind: format,
            notation,
            ...(locale !== undefined && { locale }),
            ...(formatOptions !== undefined && { options: formatOptions }),
        });
        return unit && format === 'number' ? `${text}\u00a0${unit}` : text;
    };
    return (_jsxs("div", { ...root, children: [_jsx(List, { className: "mn-bar-list__list", role: "list", "aria-label": label, children: shown.map(({ item, index }, position) => {
                    const pct = item.value === null || !(scale > 0)
                        ? 0
                        : Math.min(100, Math.max(0, (item.value / scale) * 100));
                    const labelNode = item.href ? (_jsx(LinkComponent, { className: "mn-bar-list__link", href: item.href, children: item.label })) : (item.label);
                    return (_jsxs("li", { className: "mn-bar-list__item", "data-highlighted": item.highlighted || undefined, "data-linked": item.href ? '' : undefined, children: [ranked ? (_jsx("span", { className: "mn-bar-list__rank", "aria-hidden": true, children: position + 1 })) : null, _jsxs("span", { className: "mn-bar-list__main", children: [_jsxs("span", { className: "mn-bar-list__label", children: [item.icon ? (_jsx("span", { className: "mn-bar-list__icon", "aria-hidden": true, children: item.icon })) : null, _jsx("span", { className: "mn-bar-list__text", children: labelNode })] }), item.description ? (_jsx("span", { className: "mn-bar-list__description", children: item.description })) : null] }), _jsx("span", { className: "mn-bar-list__value", "data-missing": item.value === null || undefined, children: item.displayValue ??
                                    (item.value === null ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, children: "\u2013" }), _jsx(VisuallyHidden, { children: missingLabel })] })) : (write(item.value, item))) }), _jsx("span", { className: "mn-bar-list__track", "aria-hidden": true, children: _jsx("span", { className: "mn-bar-list__bar", style: { width: `${pct}%` } }) })] }, item.id ?? index));
                }) }), shown.length < sorted.length ? (_jsx("p", { className: "mn-bar-list__footer", children: overflowLabel ?? `Top ${shown.length} of ${sorted.length}` })) : null] }));
});
//# sourceMappingURL=bar-list.js.map